import { Link } from "react-router-dom";
import { useApp } from "@/store/useApp";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { CLIENT_TYPE_LABELS, documentLabel, isPessoaFisica, legalNameLabel } from "@/lib/clientTypeLabels";
import { Building2, Mail, Phone, MapPin, User, Tag, KeyRound } from "lucide-react";

export default function Profile() {
  const { account } = useApp();
  if (!account) return null;
  if (!account.profile) {
    return (
      <div className="container max-w-4xl py-8 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Minha ficha</h1>
          <p className="text-muted-foreground mt-1">A conta foi validada, mas ainda nao possui ficha comercial completa.</p>
        </div>
        <div className="surface-card p-6">
          <p className="text-sm text-muted-foreground">
            Complete os dados comerciais no onboarding para manter a ficha atualizada.
          </p>
        </div>
      </div>
    );
  }
  const { profile, status, email } = account;
  const pessoaFisica = isPessoaFisica(profile.clientType);
  const docLabel = documentLabel(profile.clientType);
  // Para pessoa física o contato principal é a própria pessoa — só vale mostrar
  // quando o cadastro trouxer um responsável diferente do titular.
  const showContactName = !pessoaFisica || profile.contactName !== profile.companyName;

  return (
    <div className="container max-w-4xl py-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Minha ficha</h1>
        <p className="text-muted-foreground mt-1">Dados comerciais, status e tabela vinculada.</p>
      </div>

      {/* Status banner */}
      <div className="surface-card p-6 bg-gradient-subtle">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-primary flex items-center justify-center text-primary-foreground font-display text-xl font-bold shadow-glow">
              {profile.companyName.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="font-display text-xl font-bold">{profile.companyName}</h2>
              <p className="text-sm text-muted-foreground">{docLabel} {profile.cnpj}</p>
            </div>
          </div>
          <StatusBadge status={status} />
        </div>

        {status === "PENDENTE_VALIDACAO" && (
          <div className="mt-5 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
            <p className="font-semibold text-foreground">Sua ficha está em análise.</p>
            <p className="text-muted-foreground text-xs mt-0.5">
              Catálogo liberado. Preço, cotação e fechamento de pedido liberam após validação interna.
            </p>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title={pessoaFisica ? "Dados pessoais" : "Empresa"} Icon={pessoaFisica ? User : Building2}>
          <Field label={legalNameLabel(profile.clientType)} value={profile.companyName} />
          <Field label={docLabel} value={profile.cnpj} />
          <Field label="Tipo de cliente" value={CLIENT_TYPE_LABELS[profile.clientType]} />
        </Section>

        <Section title="Contato" Icon={User}>
          {showContactName && <Field label="Responsável" value={profile.contactName} />}
          <Field label="Email" value={email} Icon={Mail} />
          <Field label="Telefone" value={profile.phone} Icon={Phone} />
        </Section>

        <Section title="Entrega" Icon={MapPin}>
          <Field label="CEP" value={profile.addressCep ?? "—"} />
          <Field
            label="Endereço"
            value={[
              profile.addressLogradouro,
              profile.addressNumero ? `nº ${profile.addressNumero}` : null,
              profile.addressComplemento,
            ]
              .filter(Boolean)
              .join(", ") || "—"}
          />
          <Field
            label="Bairro / Cidade"
            value={[profile.addressBairro, profile.addressCidade && profile.addressUf
              ? `${profile.addressCidade} - ${profile.addressUf}`
              : profile.addressCidade]
              .filter(Boolean)
              .join(", ") || "—"}
          />
        </Section>

        <Section title="Comercial" Icon={Tag}>
          <Field label="Tabela vinculada" value={status === "VALIDADO" ? "Tabela Padrão B2B" : "Aguardando validação"} />
          <Field label="Condição comercial" value={status === "VALIDADO" ? "30 dias" : "—"} />
        </Section>
      </div>

      <AccessCard hasPassword={account.hasPassword === true} />
    </div>
  );
}

/** Gestão de senha da própria conta: criar (conta só-Google) ou alterar (já tem senha). */
function AccessCard({ hasPassword }: { hasPassword: boolean }) {
  return (
    <div className="surface-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="h-4 w-4 text-primary" />
        <h3 className="font-display font-semibold">Acesso e senha</h3>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {hasPassword
            ? "Você entra por e-mail e senha. Pode alterar sua senha quando quiser."
            : "Sua conta entra pelo Google. Crie uma senha para também poder entrar por e-mail — o acesso pelo Google continua funcionando."}
        </p>
        <Button asChild variant="outline" className="shrink-0">
          <Link to="/redefinir-senha">{hasPassword ? "Alterar senha" : "Criar senha"}</Link>
        </Button>
      </div>
    </div>
  );
}

function Section({ title, Icon, children }: { title: string; Icon: typeof Building2; children: React.ReactNode }) {
  return (
    <div className="surface-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-display font-semibold">{title}</h3>
      </div>
      <dl className="space-y-3">{children}</dl>
    </div>
  );
}

function Field({ label, value, Icon }: { label: string; value: string; Icon?: typeof Building2 }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium mt-0.5 inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        {value}
      </dd>
    </div>
  );
}
